import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getCurrentUser, logout } from "./lib/api";
import type { AuthState, OrganizerRole } from "./lib/types";
import { getRoute } from "./lib/routes";
import PublicHeader from "./components/PublicHeader";
import { useAutoHideHeader } from "./hooks/useAutoHideHeader";
import LandingPage from "./pages/LandingPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import AboutPage from "./pages/AboutPage";
import CheckoutCancelPage from "./pages/CheckoutCancelPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import LoginPage from "./pages/LoginPage";
import OrganizerDashboard from "./pages/OrganizerDashboard";
import OrganizerAnalyticsDashboard from "./pages/OrganizerAnalyticsDashboard";
import OwnerDashboard from "./pages/OwnerDashboard";
import ScannerPage from "./pages/ScannerPage";
import DoorTicketListPage from "./pages/DoorTicketListPage";
import TicketVerifyPage from "./pages/TicketVerifyPage";
import NotFoundPage from "./pages/NotFoundPage";

export { getRoute } from "./lib/routes";

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function hasMembership(auth: AuthState | undefined, roles: OrganizerRole[]): boolean {
  if (!auth) return false;
  if (auth.user.globalRole === "owner") return true;
  return auth.memberships.some((membership) => roles.includes(membership.role));
}

function LoginPrompt({ navigate }: { navigate: (path: string) => void }) {
  return (
    <section className="page auth-page">
      <div className="auth-panel">
        <span className="eyebrow">Protected workspace</span>
        <h1>Login required</h1>
        <p>Use an AnTix owner, organizer, or door staff account to continue.</p>
        <button className="primary-action" type="button" onClick={() => navigate("/login")}>
          Go to login
        </button>
      </div>
    </section>
  );
}

function ForbiddenPanel() {
  return (
    <section className="page auth-page">
      <div className="auth-panel">
        <span className="eyebrow">Access scoped</span>
        <h1>Not authorized</h1>
        <p>This account does not have access to that AnTix workspace.</p>
      </div>
    </section>
  );
}

export default function App() {
  const [locationKey, setLocationKey] = useState(() => `${window.location.pathname}${window.location.search}`);
  const [auth, setAuth] = useState<AuthState>();
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const onPop = () => setLocationKey(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then(setAuth)
      .finally(() => setSessionChecked(true));
  }, []);

  const location = new URL(locationKey, window.location.origin);
  const route = getRoute(location.pathname);
  const showPublicFooter = ["landing", "tickets", "ticketsPrevious", "event", "about", "login", "checkoutSuccess", "checkoutCancel", "ticket", "notFound"].includes(
    route.name,
  );
  const isPublicRoute = showPublicFooter;
  const isPublicMarketplaceRoute = ["tickets", "ticketsPrevious", "event"].includes(route.name);
  const isOwner = auth?.user.globalRole === "owner";
  const canUseOrganizerDashboard = hasMembership(auth, ["owner", "manager"]);
  const canUseScanner = hasMembership(auth, ["owner", "manager", "door_staff"]);
  const shouldHideHeader = useAutoHideHeader(isPublicRoute);
  const publicHeaderAuth = useMemo(
    () => ({
      auth,
      canUseOrganizerDashboard,
      canUseScanner,
      isOwner,
    }),
    [auth, canUseOrganizerDashboard, canUseScanner, isOwner],
  );
  const workspaceNavigationAccess = publicHeaderAuth;

  async function handleLogout() {
    await logout().catch(() => undefined);
    setAuth(undefined);
    navigate("/");
  }

  function protectedContent(content: ReactNode, allowed: boolean) {
    if (!sessionChecked) {
      return (
        <section className="page auth-page">
          <div className="auth-panel">
            <span className="eyebrow">Checking session</span>
            <h1>One moment</h1>
          </div>
        </section>
      );
    }
    if (!auth) return <LoginPrompt navigate={navigate} />;
    if (!allowed) return <ForbiddenPanel />;
    return content;
  }

  return (
    <div className={`app-shell${isPublicRoute ? " app-shell--public" : " app-shell--workspace"}`}>
      <PublicHeader
        navigate={navigate}
        onLogout={handleLogout}
        locationKey={location.pathname}
        hidden={shouldHideHeader}
        {...publicHeaderAuth}
      />

      <main>
        {route.name === "landing" && <LandingPage navigate={navigate} />}
        {route.name === "tickets" && <EventsPage navigate={navigate} mode="current" />}
        {route.name === "ticketsPrevious" && <EventsPage navigate={navigate} mode="previous" />}
        {route.name === "event" && <EventDetailPage slug={route.slug ?? "neon-rooftop"} />}
        {route.name === "about" && <AboutPage />}
        {route.name === "checkoutSuccess" && <CheckoutSuccessPage navigate={navigate} />}
        {route.name === "checkoutCancel" && <CheckoutCancelPage navigate={navigate} />}
        {route.name === "ticket" && <TicketVerifyPage ticketCode={route.ticketCode ?? ""} />}
        {route.name === "notFound" && <NotFoundPage navigate={navigate} />}
        {route.name === "login" && <LoginPage navigate={navigate} onLogin={setAuth} />}
        {route.name === "app" &&
          protectedContent(
            route.appPath === "/app"
              ? <OrganizerAnalyticsDashboard navigate={navigate} search={location.search} workspaceNavigationAccess={workspaceNavigationAccess} />
              : <OrganizerDashboard navigate={navigate} path={route.appPath ?? "/app/events"} workspaceNavigationAccess={workspaceNavigationAccess} />,
            canUseOrganizerDashboard
          )}
        {route.name === "owner" && protectedContent(<OwnerDashboard navigate={navigate} path={route.ownerPath ?? "/owner"} workspaceNavigationAccess={workspaceNavigationAccess} />, Boolean(isOwner))}
        {route.name === "scanner" && protectedContent(<ScannerPage navigate={navigate} workspaceNavigationAccess={workspaceNavigationAccess} />, canUseScanner)}
        {route.name === "doorTickets" && protectedContent(<DoorTicketListPage navigate={navigate} workspaceNavigationAccess={workspaceNavigationAccess} />, canUseScanner)}
      </main>

      {showPublicFooter && (
        <footer className="site-footer">
          <div className="site-footer__inner">
            <p className="site-footer__copy">Operated by ACS Studios, a brand of SPICE PRODUCTIONS LLC.</p>
            <nav className="site-footer__nav" aria-label="Public links">
              <a className="site-footer__link" href="/tickets">Events</a>
              <a className="site-footer__link" href="/tickets/previous">Previous Events</a>
              <a className="site-footer__link" href="/about">About AnTix</a>
              <a className="site-footer__link" href="https://acsstudios.co/privacy">
                Privacy Policy
              </a>
              <a className="site-footer__link" href="https://acsstudios.co/terms">
                Terms of Service
              </a>
            </nav>
          </div>
        </footer>
      )}
    </div>
  );
}
